import moment from 'moment';
import { Formats } from 'shovel-lib/types';

import { Theme } from '../../assets/themes';

/**
 * What the student picked for light or dark: one of them; Auto, which follows the device's own setting; or set hours,
 * dark between two times of day (Petr, 2026-10-08). Kept in this browser only, under the THEME key that has always held
 * 'light' or 'dark'.
 */
export type ThemePreference = Theme | 'auto' | 'scheduled';

/** When set hours turn dark and back to light, in minutes after midnight. */
export type DarkHours = { from: number; to: number };

export const DEFAULT_DARK_HOURS: DarkHours = { from: 20 * 60, to: 7 * 60 };

const MINUTES_IN_DAY = 24 * 60;

export const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

/** Sent with the link to Settings, so the page opens at its Appearance section rather than its top. */
export const SCROLL_TO_APPEARANCE = 'appearance';

// Anything not saved by this or earlier code, or nothing at all, is light, as it always was.
export const parseThemePreference = (stored: string | null): ThemePreference =>
  stored === 'dark' || stored === 'auto' || stored === 'scheduled' ? stored : 'light';

const isMinuteOfDay = (value: unknown): value is number =>
  Number.isInteger(value) && (value as number) >= 0 && (value as number) < MINUTES_IN_DAY;

export const parseDarkHours = (stored: string | null): DarkHours => {
  try {
    const parsed = stored ? JSON.parse(stored) : null;
    if (parsed && isMinuteOfDay(parsed.from) && isMinuteOfDay(parsed.to)) return { from: parsed.from, to: parsed.to };
  } catch (e) {
    // Unreadable, so the defaults.
  }
  return DEFAULT_DARK_HOURS;
};

export const minuteOfDay = (date: Date = new Date()) => date.getHours() * 60 + date.getMinutes();

// Dark from `from` up to `to`, across midnight when it starts later than it ends. The same time twice is never dark.
export const isDarkHour = (minute: number, { from, to }: DarkHours) =>
  from < to ? minute >= from && minute < to : from > to && (minute >= from || minute < to);

/** How long until set hours next turn dark or light, so the page changes on the minute. */
export const msUntilNextChange = (date: Date, { from, to }: DarkHours) => {
  const minute = minuteOfDay(date);
  const minutesAhead = Math.min(
    ...[from, to].map(boundary => (boundary - minute + MINUTES_IN_DAY) % MINUTES_IN_DAY || MINUTES_IN_DAY)
  );
  return minutesAhead * 60000 - date.getSeconds() * 1000 - date.getMilliseconds();
};

export const systemPrefersDark = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(DARK_SCHEME_QUERY).matches;

/** What shows: what was picked, or what the device or the time of day says for Auto and set hours. */
export const resolveTheme = (preference: ThemePreference, systemDark: boolean, inDarkHours: boolean): Theme => {
  if (preference === 'auto') return systemDark ? 'dark' : 'light';
  if (preference === 'scheduled') return inDarkHours ? 'dark' : 'light';
  return preference;
};

/**
 * Set hours are on the device's clock, where the student is, not the term's time zone the calendar shows (Petr,
 * 2026-10-08): dark at 8pm is about evening where they are.
 */
export const minuteOfDayToDate = (minute: number) => {
  const date = new Date();
  date.setHours(Math.floor(minute / 60), minute % 60, 0, 0);
  return date;
};

// In the student's own time format, as the rest of the app shows times.
export const formatMinuteOfDay = (minute: number) =>
  moment(minuteOfDayToDate(minute))
    .local()
    .format(Formats.LOWERCASE_TIME);

// The app's time fields show and return a time in the term's zone, as everything else in the app does. Going to and
// from them through that zone keeps the hour picked the hour kept; read straight off the device's clock, a picked 8pm
// was saved as another hour whenever the two zones differed.
export const toTimeFieldValue = (minute: number) =>
  moment()
    .startOf('day')
    .add(minute, 'minutes')
    .toDate();

export const fromTimeFieldValue = (date: Date) => {
  const time = moment(date);
  return time.hours() * 60 + time.minutes();
};
